import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Check, ChevronLeft } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { InsuranceLogo } from "@/components/forms/InsuranceLogo";
import {
  listInsuranceCatalogFn,
  cleanPlanName,
  SELF_PAY_ID,
  type CatalogCarrier,
} from "@/lib/insurance-catalog.functions";
import { cn } from "@/lib/utils";

/**
 * Patient-facing insurance picker: carrier first, then plan.
 *
 * Two steps rather than one flat list, because after the CMS import the catalog
 * holds ~3.4k plans — a single list of those is unnavigable, while a patient
 * always knows their carrier before their plan name.
 *
 * Popular carriers are surfaced as a logo grid on top and every carrier is
 * repeated below under A-Z headings, so the common case is one tap and the long
 * tail is still reachable by scrolling. Ordering comes from
 * insurance_carriers.popularity, so it is tuned in the database, not in code.
 *
 * Selection returns a single token that goes straight into the existing
 * `_insurance` search filter:
 *   carrier only  -> "aetna"
 *   carrier+plan  -> "aetna:choice-pos-ii"
 *   self-pay      -> "selfpay"
 */
export function InsurancePickerDialog({
  open,
  onOpenChange,
  value,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current token, or null when no insurance filter is applied. */
  value: string | null;
  /**
   * The chosen token, plus the name to display for it. The label is handed back
   * because only this component has the catalog loaded — callers that just need
   * to show what was picked would otherwise have to fetch all 3.4k plans again.
   */
  onSelect: (token: string | null, label?: string) => void;
}) {
  const [query, setQuery] = useState("");
  // Which carrier's plan list is showing; null = the carrier list.
  const [drilldown, setDrilldown] = useState<CatalogCarrier | null>(null);

  const { data } = useQuery({
    queryKey: ["insurance-catalog"],
    queryFn: () => listInsuranceCatalogFn(),
    staleTime: 5 * 60_000,
  });

  // Memoized so the `?? []` fallback doesn't mint a new array each render and
  // invalidate every downstream useMemo.
  const carriers = useMemo(() => data?.carriers ?? [], [data]);
  const q = query.trim().toLowerCase();

  const matching = useMemo(
    () => (q ? carriers.filter((c) => c.name.toLowerCase().includes(q)) : carriers),
    [carriers, q],
  );

  // The grid mirrors Zocdoc's "Popular carriers": the highest-popularity rows
  // that also have a real logo, since a grid of identical fallback shields
  // communicates nothing. Hidden while searching — the query is the filter then.
  const popular = useMemo(
    () => (q ? [] : carriers.slice(0, 8)),
    [carriers, q],
  );

  // Alphabetical sections, digits collapsed under '#', matching the reference UI.
  const sections = useMemo(() => {
    const byLetter = new Map<string, CatalogCarrier[]>();
    for (const c of [...matching].sort((a, b) => a.name.localeCompare(b.name))) {
      const first = c.name[0]?.toUpperCase() ?? "#";
      const key = /[A-Z]/.test(first) ? first : "#";
      const list = byLetter.get(key);
      if (list) list.push(c);
      else byLetter.set(key, [c]);
    }
    return [...byLetter.entries()].sort(([a], [b]) =>
      a === "#" ? -1 : b === "#" ? 1 : a.localeCompare(b),
    );
  }, [matching]);

  const drilldownPlans = useMemo(
    () =>
      drilldown ? (data?.plans ?? []).filter((p) => p.carrier_id === drilldown.id) : [],
    [data, drilldown],
  );

  const close = () => {
    onOpenChange(false);
    // Reset only after closing so the list does not flicker during the animation.
    setTimeout(() => {
      setQuery("");
      setDrilldown(null);
    }, 150);
  };

  const pickCarrier = (c: CatalogCarrier) => {
    const plans = (data?.plans ?? []).filter((p) => p.carrier_id === c.id);
    // No plans on file: the carrier token is the whole answer, so don't make the
    // patient tap through an empty second step.
    if (plans.length === 0) {
      onSelect(c.id, c.name);
      close();
      return;
    }
    setQuery("");
    setDrilldown(c);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-2xl font-semibold flex items-center gap-2">
            {drilldown && (
              <button
                type="button"
                onClick={() => setDrilldown(null)}
                aria-label="Back to carriers"
                className="-ml-1 p-1 rounded hover:bg-muted"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            {drilldown ? drilldown.name : "Select insurance"}
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={drilldown ? "Search plans" : "Search for insurance"}
              className="pl-9 h-11 rounded-full"
              aria-label={drilldown ? "Search plans" : "Search for insurance"}
            />
          </div>
        </div>

        <div className="max-h-[55vh] overflow-y-auto px-6 pb-2">
          {drilldown ? (
            <ul className="py-1">
              {/* Accepting the carrier without naming a plan stays available —
                  most patients know the carrier but not the exact plan name. */}
              <li>
                <Row
                  label={`All ${drilldown.short} plans`}
                  selected={value === drilldown.id}
                  onClick={() => {
                    onSelect(drilldown.id, drilldown.name);
                    close();
                  }}
                />
              </li>
              {/* No render cap: the largest carrier now has ~360 Medicare
                  products, and a silent slice would hide the tail from anyone
                  scrolling rather than searching. Plain rows at this count are
                  cheap; revisit with virtualization only if a carrier grows
                  past a few thousand. */}
              {drilldownPlans
                .filter((p) => !q || p.name.toLowerCase().includes(q))
                .map((p) => (
                  <li key={p.id}>
                    {(() => {
                      const label = cleanPlanName(p.name, drilldown, p.plan_type, p.metal_level);
                      return (
                        <Row
                          label={label}
                          badges={[p.metal_level, p.plan_type]}
                          selected={value === p.id}
                          onClick={() => {
                            onSelect(p.id, label);
                            close();
                          }}
                        />
                      );
                    })()}
                  </li>
                ))}
            </ul>
          ) : (
            <>
              {popular.length > 0 && (
                <>
                  <h3 className="text-base font-semibold mb-3">Popular carriers</h3>
                  <div className="grid grid-cols-2 gap-2 mb-5">
                    {popular.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => pickCarrier(c)}
                        className={cn(
                          "flex items-center gap-3 p-2 rounded-lg border text-left hover:bg-muted/60 transition-colors",
                          value?.split(":")[0] === c.id ? "border-primary" : "border-border",
                        )}
                      >
                        <InsuranceLogo id={c.id} color={c.color} className="h-10 w-10" />
                        <span className="text-sm leading-tight">{c.name}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}

              <h3 className="text-base font-semibold mb-1">
                {q ? "Matching carriers" : "All carriers"}
              </h3>
              {sections.length === 0 && (
                <p className="py-6 text-sm text-muted-foreground">
                  No carriers match “{query.trim()}”.
                </p>
              )}
              {sections.map(([letter, list]) => (
                <div key={letter}>
                  <div className="text-xs text-muted-foreground pt-3 pb-1">{letter}</div>
                  <ul>
                    {list.map((c) => (
                      <li key={c.id}>
                        <Row
                          label={c.name}
                          selected={value?.split(":")[0] === c.id}
                          onClick={() => pickCarrier(c)}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="border-t border-border py-4 text-center">
          <button
            type="button"
            onClick={() => {
              onSelect(SELF_PAY_ID, "Paying for myself");
              close();
            }}
            className="text-sm underline underline-offset-4 hover:no-underline"
          >
            I&rsquo;m paying for myself
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({
  label,
  badges,
  selected,
  onClick,
}: {
  label: string;
  /** Metal level / plan type, rendered as chips instead of inline text. */
  badges?: (string | null | undefined)[];
  selected: boolean;
  onClick: () => void;
}) {
  const chips = (badges ?? []).filter((b): b is string => Boolean(b?.trim()));
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-start justify-between gap-3 py-2.5 text-left text-sm hover:bg-muted/60 rounded px-1"
    >
      <span className="min-w-0">
        <span className="block">{label}</span>
        {chips.length > 0 && (
          <span className="mt-1 flex flex-wrap gap-1">
            {chips.map((c) => (
              <span
                key={c}
                className="inline-block rounded-sm border border-border bg-muted/50 px-1.5 py-0.5 text-[11px] leading-none text-muted-foreground"
              >
                {c}
              </span>
            ))}
          </span>
        )}
      </span>
      {selected && <Check className="h-4 w-4 shrink-0 text-primary mt-0.5" />}
    </button>
  );
}
